import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Activity,
  Rocket,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  Code2,
  CheckCircle2,
  Calculator,
  MessageSquare,
  Users,
  Search,
} from "lucide-react";

import SEO from "@/components/SEO";
import HeroVideo from "@/components/HeroVideo";
import { MarketplaceLogoStrip, MarketplaceGrid } from "@/components/MarketplaceLogos";
import Testimonials from "@/components/Testimonials";
import GoogleReviews from "@/components/GoogleReviews";
import CtaBanner from "@/components/CtaBanner";
import AuthorizedPartners from "@/components/AuthorizedPartners";
import CocobluSection from "@/components/CocobluSection";
import ProjectsSection from "@/components/ProjectsSection";
import QuizSection from "@/components/QuizSection";
import FaqAccordion from "@/components/FaqAccordion";
import {
  SERVICES,
  PREMIUM_MARKETPLACES,
  QUICK_COMMERCE,
  WHY_CHOOSE_US,
} from "@/data/content";

const ICONS = { Activity, Rocket, Sparkles, TrendingUp, ShieldCheck, Code2 };

export default function Home() {
  return (
    <>
      <SEO
        title="Launch, Manage & Scale Your E-commerce Business"
        description="Orina Enterprise — premium e-commerce account management & seller onboarding agency in India. Scale on Amazon, Flipkart, Meesho, JioMart, Nykaa, Myntra, Blinkit, Zepto with expert managers."
        keywords="E-commerce account management India, Amazon seller onboarding India, Flipkart account management, JioMart onboarding service, Marketplace management agency India"
        path="/"
      />

      {/* HERO with autoplay video */}
      <HeroVideo />

      {/* TRUSTED MARKETPLACES STRIP */}
      <section className="bg-[#FAFAF7]">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
          <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-black/60">
            Trusted Marketplace Expertise Across India
          </p>
        </div>
        <MarketplaceLogoStrip />
      </section>

      {/* AUTHORIZED PARTNER */}
      <AuthorizedPartners />

      {/* SERVICES GRID */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="eyebrow">Our Services</div>
              <h2 className="display mt-3 text-4xl font-black tracking-tight md:text-5xl lg:text-6xl">
                Everything you need.<br />Nothing you don't.
              </h2>
            </div>
            <p className="max-w-md text-base text-black/70">
              Six specialised practices, one accountable team. Built for India's marketplace landscape.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-0 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || Activity;
              return (
                <Link
                  to={s.id === "web-app" ? "/web-app-development/" : "/services/"}
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className="group relative -ml-[2px] -mt-[2px] flex flex-col border-2 border-black bg-white p-8 transition-all hover:bg-[#FFD700]"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center border-2 border-black bg-[#FFD700] group-hover:bg-black group-hover:text-[#FFD700]">
                      <Icon size={22} />
                    </div>
                    <ArrowUpRight size={20} className="opacity-50 group-hover:opacity-100" />
                  </div>
                  <h3 className="display mt-6 text-2xl font-black tracking-tight">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-black/70 group-hover:text-black/90">
                    {s.short}
                  </p>
                  <div className="mt-5 text-[11px] font-bold uppercase tracking-widest text-black/60 group-hover:text-black">
                    0{i + 1} / 0{SERVICES.length}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PREMIUM MARKETPLACES */}
      <section id="premium" className="bg-black py-20 text-white md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FFD700]">Premium Marketplaces</div>
              <h2 className="display mt-3 text-4xl font-black tracking-tight md:text-5xl">
                Build a Strong<br />Brand Presence.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-white/75">
                Nykaa, Myntra, Ajio Luxe and Tata Cliq are curated platforms with strict entry — but they bring high-value customers and premium brand positioning.
              </p>
              <ul className="mt-6 space-y-2 text-sm">
                {[
                  "Brand approval support",
                  "Compliance & quality control",
                  "Premium catalog production",
                  "Ad strategy for premium audiences",
                  "Growth management",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#FFD700]" /> {b}
                  </li>
                ))}
              </ul>
              <Link to="/premium-marketplaces/" className="btn-brutal dark mt-8 inline-flex">
                Explore Premium <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="md:col-span-7">
              <MarketplaceGrid items={PREMIUM_MARKETPLACES} dark />
            </div>
          </div>
        </div>
      </section>

      {/* QUICK COMMERCE */}
      <section id="quick" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="order-2 md:col-span-7 md:order-1">
              <MarketplaceGrid items={QUICK_COMMERCE} />
            </div>
            <div className="order-1 md:col-span-5 md:order-2">
              <div className="eyebrow">Quick Commerce</div>
              <h2 className="display mt-3 text-4xl font-black tracking-tight md:text-5xl">
                Sell Faster.<br />Deliver Faster.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-black/75">
                Blinkit, Zepto and Instamart deliver in 10–30 minutes. The model rewards fast-moving SKUs, sharp pricing and disciplined inventory.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-black/80">
                {[
                  "Vendor onboarding",
                  "SKU & pack-size strategy",
                  "Inventory planning",
                  "Pricing for impulse buys",
                  "Growth roadmap & ads",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-black" /> {b}
                  </li>
                ))}
              </ul>
              <Link to="/quick-commerce/" className="btn-brutal mt-8 inline-flex">
                See Quick Commerce <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INTERNATIONAL — USA & UAE */}
      <section className="border-y-2 border-black bg-white py-20 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-5">
            <div className="eyebrow">India · USA · UAE</div>
            <h2 className="display mt-3 text-4xl font-black tracking-tight md:text-5xl">
              Selling in the USA or Dubai?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-black/75">
              Orina Enterprise is a Mumbai-based e-commerce agency. Besides India's marketplaces, we manage Amazon.com, Walmart and Shopify for brands in the USA, and Amazon.ae and Noon for brands in Dubai and the UAE, including Indian brands expanding abroad.
            </p>
          </div>
          <div className="grid gap-0 md:col-span-7 md:grid-cols-2">
            {[
              { to: "/ecommerce-services-usa/", t: "USA", d: "Amazon.com, Walmart Marketplace and Shopify account management, ads and websites." },
              { to: "/ecommerce-services-uae/", t: "Dubai & UAE", d: "Amazon.ae and Noon onboarding, English and Arabic listings, ads and fulfilment planning." },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="group -ml-[2px] -mt-[2px] flex flex-col border-2 border-black bg-[#FFD700] p-8 transition-colors hover:bg-black hover:text-[#FFD700]"
              >
                <div className="flex items-center justify-between">
                  <h3 className="display text-2xl font-black">{c.t}</h3>
                  <ArrowUpRight size={20} />
                </div>
                <p className="mt-3 text-sm leading-relaxed">{c.d}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CRM + MANPOWER + EXTRACTOR — Other solutions */}
      <section className="border-y-2 border-black bg-[#FAFAF7] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="eyebrow">Beyond Marketplaces</div>
          <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl lg:text-5xl">
            CRM. Manpower. SaaS tools.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-0 md:grid-cols-3">
            {[
              { icon: MessageSquare, t: "CRM Solutions", d: "WhatsApp CRM, Worknex & Earn Yatra — for leads, ops and rewards.", to: "/crm-solutions/" },
              { icon: Users, t: "Manpower Solutions", d: "Field teams, sales staffing and operations across 100+ cities.", to: "/manpower-solutions/" },
              { icon: Search, t: "Google Extractor Tool", d: "Extract thousands of business leads from Google in minutes.", to: "/google-extractor/" },
            ].map((c, i) => (
              <Link
                key={c.t}
                to={c.to}
                data-testid={`other-solution-${i}`}
                className="-ml-[2px] -mt-[2px] flex flex-col border-2 border-black bg-white p-8 transition-all hover:bg-[#FFD700]"
              >
                <div className="flex h-12 w-12 items-center justify-center border-2 border-black bg-[#FFD700]">
                  <c.icon size={20} />
                </div>
                <h3 className="display mt-6 text-2xl font-black">{c.t}</h3>
                <p className="mt-3 flex-1 text-sm text-black/70">{c.d}</p>
                <div className="mt-6 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest">
                  Explore <ArrowUpRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* COCOBLU partner spotlight */}
      <CocobluSection />

      {/* PROJECTS / PORTFOLIO */}
      <ProjectsSection />

      {/* WHY CHOOSE US */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="eyebrow">Why Orina</div>
              <h2 className="display mt-3 text-4xl font-black tracking-tight md:text-5xl">
                Built for outcomes.<br />Wired for execution.
              </h2>
            </div>
            <p className="max-w-md text-base text-black/70">
              Most agencies sell hours. We sell outcomes. Here's what that means in practice.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-0 md:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE_US.map((w, i) => (
              <div
                key={w.title}
                data-testid={`why-${i}`}
                className="-ml-[2px] -mt-[2px] flex items-start gap-4 border-2 border-black bg-white p-7 transition-colors hover:bg-[#FFD700]"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-black bg-black font-black text-[#FFD700]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="display text-lg font-black tracking-tight">{w.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-black/70">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#FAFAF7] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="eyebrow">Client Stories</div>
              <h2 className="display mt-3 text-4xl font-black tracking-tight md:text-5xl">
                Brands that scale<br />don't pick agencies. They pick partners.
              </h2>
            </div>
            <Link to="/testimonials/" className="btn-brutal secondary self-start" data-testid="testimonials-cta">
              All testimonials <ArrowRight size={16} />
            </Link>
          </div>
          <div className="mt-12">
            <Testimonials limit={3} />
          </div>
          <div className="mt-12">
            <GoogleReviews />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-y-2 border-black bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <div className="eyebrow">FAQ</div>
              <h2 className="display mt-3 text-4xl font-black tracking-tight md:text-5xl">
                Quick answers.
              </h2>
              <p className="mt-4 text-base text-black/70">
                More on the <Link to="/faq/" className="underline decoration-[#FFD700] decoration-4 underline-offset-4">full FAQ page</Link>.
              </p>
              <Link to="/calculators/" className="btn-brutal secondary mt-6 inline-flex">
                <Calculator size={14} /> Try our Calculators
              </Link>
            </div>
            <div className="md:col-span-8">
              <FaqAccordion limit={6} />
            </div>
          </div>
        </div>
      </section>

      {/* QUIZ */}
      <QuizSection />

      {/* CTA */}
      <CtaBanner />
    </>
  );
}
